import type { SSEEvent, InvokeAgentOptions, AgentName } from '../apis/AgentCoreAPI';
import { AGENT_NAMES } from '../apis/AgentCoreAPI';
import BaseService from './BaseService';

export { AGENT_NAMES };
export type { AgentName };

export type AgentMessage = {
  role: string;
  content: string;
};

export type InvokeOptions = {
  /** Agent to invoke (defaults to clinical_workflow_chat) */
  agentName?: AgentName;
  /** Chat messages to send to the agent */
  messages: AgentMessage[];
  /** Enable SSE streaming (default: true) */
  stream?: boolean;
  /** Session ID for multi-turn conversation. Auto-generated if not provided. */
  sessionId?: string;
  /** Organization ID (required for OAuth token exchange) */
  organizationId: string;
  /** AbortSignal for cancellation */
  signal?: AbortSignal;
};

export default class AgentCoreService extends BaseService {
  /**
   * Invoke the clinical workflow agent and stream SSE events via callback.
   *
   * Usage:
   * ```typescript
   * await client.agentCore.invoke({
   *   messages: [{ role: 'user', content: 'Configure Nurse Call workflow' }],
   *   organizationId: 'org-token-here',
   *   sessionId: 'session-uuid',
   * }, (event) => {
   *   if (event.event === 'token') {
   *     appendText(event.data.text);
   *   }
   * });
   * ```
   */
  async invoke(options: InvokeOptions, onEvent: (event: SSEEvent) => void): Promise<void> {
    return this.host.api.agentCore.invoke(options as InvokeAgentOptions, onEvent);
  }

  /**
   * Get the agent invocation URL for the current environment.
   */
  getInvocationUrl(agentName?: AgentName): string {
    return this.host.api.agentCore.getInvocationUrl(agentName);
  }

  /**
   * Exchange API key/secret for an OAuth JWT via the IDP.
   */
  async getOAuthToken(organizationId: string): Promise<string> {
    return this.host.api.agentCore.getOAuthToken(organizationId);
  }

  clearTokenCache(organizationId?: string): void {
    this.host.api.agentCore.clearTokenCache(organizationId);
  }

  dispose(): void {
    this.host.api.agentCore.dispose();
  }
}
